import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  TextField,
  Dialog,
  DialogContent,
  Switch,
  Checkbox,
  FormControlLabel
} from "@mui/material";
import {
  Palette as PaletteIcon,
  Image as ImageIcon,
  Title as TitleIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  FitnessCenter as WorkoutIcon,
  CalendarMonth as WeekIcon,
  Add as AddIcon
} from "@mui/icons-material";
import {useState, useMemo, useEffect} from "react";
import {useFormik} from "formik";
import * as ownCompanyService from "../../services/CompanyService";
import * as systemCompanyService from "../../services/system/CompanyService";
import {useMessageContext} from "../../contexts/MessageContext";
import {useAuth} from "../../contexts/AuthContext";
import {getErrorMessage} from "../../enums/ErrorMessages";
import {getApiErrorCode} from "../../utils/apiError";
import {RTL} from "../../cache/CacheRtl";
import {t} from "../dashboard/coach/coachTokens";
import BrandingPhonePreview from "./BrandingPhonePreview";

const inputSx = {
    "& .MuiOutlinedInput-root": {
        borderRadius: t.rs,
        backgroundColor: t.white,
        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: t.green },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: t.green, borderWidth: "2px" }
    },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: t.border }
};

const labelSx = { fontSize: "13px", fontWeight: 600, color: t.t2, marginBottom: "6px" };


const WORKOUT_IMAGE_SLOTS = [
  { key: "0", label: "אימון 1" },
  { key: "1", label: "אימון 2" },
  { key: "2", label: "אימון 3" },
  { key: "3", label: "אימון 4" },
  { key: "4", label: "אימון 5" },
  { key: "5", label: "אימון 6" },
  { key: "6", label: "אימון 7" },
];

const CompanyBrandingSection = ({company, setLoadCompany, editOpen, setEditOpen}) => {
  const [logoPreview, setLogoPreview] = useState(null);
  const [backgroundPreview, setBackgroundPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [backgroundFile, setBackgroundFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [removeBackground, setRemoveBackground] = useState(false);

  // Branding images state: { "WEEK:0": { file, preview, remove }, "WORKOUT:0": { file, preview, remove } }
  const [imageSlots, setImageSlots] = useState({});
  // New week image slots added during this edit session (not yet saved)
  const [newWeekSlots, setNewWeekSlots] = useState([]);

  const {notificationHandler} = useMessageContext();
  const {isSuperAdmin} = useAuth();
  const canEditBundleId = isSuperAdmin();

  // UI-only white-label toggle. Seeded from whether the company already has any
  // white-label config; drives visibility of the bundle id + store URL fields.
  // Does NOT clear values on the server when unchecked — purely cosmetic gating.
  const [isWhiteLabel, setIsWhiteLabel] = useState(
    Boolean(company?.bundleId || company?.appleUrl || company?.googleUrl)
  );

  // Re-seed the toggle whenever a different company's dialog is opened.
  useEffect(() => {
    setIsWhiteLabel(Boolean(company?.bundleId || company?.appleUrl || company?.googleUrl));
  }, [company?.id, company?.bundleId, company?.appleUrl, company?.googleUrl, editOpen]);

  const brandingImagesMap = useMemo(() => {
    const map = {};
    (company?.brandingImages || []).forEach(img => {
      map[`${img.imageType}:${img.imageKey}`] = img.imageUrl;
    });
    return map;
  }, [company?.brandingImages]);

  // Existing week images from the server, sorted by numeric key
  const existingWeekImages = useMemo(() => {
    return (company?.brandingImages || [])
        .filter(img => img.imageType === "WEEK")
        .sort((a, b) => parseInt(a.imageKey) - parseInt(b.imageKey));
  }, [company?.brandingImages]);

  // Next available week image index
  const nextWeekImageIndex = existingWeekImages.length + newWeekSlots.length;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: company?.id || null,
      name: company?.name || '',
      address: company?.address || '',
      brandingColor: company?.brandingColor || '#07BBA6',
      brandingAppName: company?.brandingAppName || '',
      bundleId: company?.bundleId || '',
      appleUrl: company?.appleUrl || '',
      googleUrl: company?.googleUrl || '',
      minVersionIos: company?.minVersionIos || '',
      minVersionAndroid: company?.minVersionAndroid || '',
      showWorkoutImages: company?.showWorkoutImages ?? true,
    },
    onSubmit: async (values) => {
      setSaving(true);
      try {
        // Which endpoints this form uses depends on WHO is saving, because the card renders on two
        // pages, and that applies to the IMAGE calls below just as much as to the JSON save:
        //   - CompanyPage (super-admin drilling into a company by id) → /system/company/{id}/*,
        //     which needs the id since a super-admin has no company of their own to scope to.
        //   - SettingPage (a company admin editing their OWN company) → /admin/company/*, scoped
        //     from the principal.
        // The two families are not interchangeable: /system is SYSTEM_ADMIN-only (a company admin
        // got a 401), and /admin is ADMIN-only AND resolves the company from the principal, so it
        // 403s for a company-less super-admin. Picking one family for the whole save is what keeps
        // it all-or-nothing — routing the JSON half to /system while the images stayed on /admin is
        // exactly what made a super-admin's logo update fail after the text fields had saved.
        // Same split as CompanySettingsSection.
        const api = isSuperAdmin() ? systemCompanyService : ownCompanyService;

        // The white-label fields (bundleId, store URLs, min versions) go only on the /system path:
        // the server's company-admin DTO has no such fields and drops them, so sending them from
        // here would silently no-op. `canEditBundleId` already hides them from a company admin.
        const {id, bundleId, appleUrl, googleUrl, minVersionIos, minVersionAndroid, ...ownBranding} =
            values;
        await (isSuperAdmin()
            ? systemCompanyService.updateCompanyBranding(id, {
              ...ownBranding,
              bundleId,
              appleUrl,
              googleUrl,
              minVersionIos,
              minVersionAndroid,
            })
            : ownCompanyService.updateOwnCompanyBranding(ownBranding));

        if (removeLogo) {
          await api.removeBrandingLogo(company.id);
        } else if (logoFile) {
          await api.uploadBrandingLogo(company.id, logoFile);
        }

        if (removeBackground) {
          await api.removeBrandingBackground(company.id);
        } else if (backgroundFile) {
          await api.uploadBrandingBackground(company.id, backgroundFile);
        }

        // Remove week images with reindexing (process removals first, highest key first to keep indices valid)
        const weekRemovals = Object.entries(imageSlots)
            .filter(([key, slot]) => key.startsWith("WEEK:") && slot.remove)
            .sort((a, b) => parseInt(b[0].split(":")[1]) - parseInt(a[0].split(":")[1]));
        for (const [slotKey] of weekRemovals) {
          const imageKey = slotKey.split(":")[1];
          await api.removeWeekImage(company.id, imageKey);
        }

        // Upload updated week images (existing slots that got a new file)
        const weekUploads = Object.entries(imageSlots)
            .filter(([key, slot]) => key.startsWith("WEEK:") && slot.file && !slot.remove);
        for (const [slotKey, slot] of weekUploads) {
          const imageKey = slotKey.split(":")[1];
          await api.uploadBrandingImage(company.id, "WEEK", imageKey, slot.file);
        }

        // Upload new week image slots
        for (const slot of newWeekSlots) {
          if (slot.file) {
            await api.uploadBrandingImage(company.id, "WEEK", slot.key, slot.file);
          }
        }

        // Upload/remove workout branding images
        for (const [slotKey, slot] of Object.entries(imageSlots)) {
          const [imageType, imageKey] = slotKey.split(":");
          if (imageType === "WEEK") continue;
          if (slot.remove) {
            await api.removeBrandingImage(company.id, imageType, imageKey);
          } else if (slot.file) {
            await api.uploadBrandingImage(company.id, imageType, imageKey, slot.file);
          }
        }

        notificationHandler.success('מיתוג עודכן בהצלחה');
        setLoadCompany(prv => !prv);
        handleClose();
      } catch (e) {
        // Both rejection shapes reach here now: the /admin family goes through axios (code under
        // `response.data`), while the /system family is on the generated client, which rejects with
        // the body itself. Reading only the axios shape would report the generic message for every
        // super-admin failure.
        notificationHandler.error(getErrorMessage(getApiErrorCode(e)));
      } finally {
        setSaving(false);
      }
    }
  });

  const handleClose = () => {
    formik.resetForm();
    setLogoPreview(null);
    setBackgroundPreview(null);
    setLogoFile(null);
    setBackgroundFile(null);
    setRemoveLogo(false);
    setRemoveBackground(false);
    setImageSlots({});
    setNewWeekSlots([]);
    setEditOpen(false);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
      setRemoveLogo(false);
    }
  };

  const handleBackgroundUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBackgroundFile(file);
      setBackgroundPreview(URL.createObjectURL(file));
      setRemoveBackground(false);
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setRemoveLogo(true);
  };

  const handleRemoveBackground = () => {
    setBackgroundFile(null);
    setBackgroundPreview(null);
    setRemoveBackground(true);
  };

  const handleSlotUpload = (imageType, imageKey, e) => {
    const file = e.target.files[0];
    if (file) {
      setImageSlots(prev => ({
        ...prev,
        [`${imageType}:${imageKey}`]: { file, preview: URL.createObjectURL(file), remove: false }
      }));
    }
  };

  const handleSlotRemove = (imageType, imageKey) => {
    setImageSlots(prev => ({
      ...prev,
      [`${imageType}:${imageKey}`]: { file: null, preview: null, remove: true }
    }));
  };

  const getSlotImageUrl = (imageType, imageKey) => {
    const slotKey = `${imageType}:${imageKey}`;
    const slot = imageSlots[slotKey];
    if (slot?.remove) return null;
    if (slot?.preview) return slot.preview;
    return brandingImagesMap[slotKey] || null;
  };

  const renderImageSlot = (imageType, imageKey, label) => {
    const currentUrl = getSlotImageUrl(imageType, imageKey);
    const slotKey = `${imageType}:${imageKey}`;
    const isMarkedForRemoval = imageSlots[slotKey]?.remove;

    return (
      <Box key={slotKey}>
        <Typography sx={labelSx}>{label}</Typography>
        <Box sx={{display: "flex", alignItems: "center", gap: 2}}>
          <Button variant="outlined" component="label"
            sx={{
              minWidth: 100, borderColor: t.border, color: t.t2,
              fontSize: "13px", borderRadius: t.rs, textTransform: "none",
              "&:hover": { borderColor: t.green, color: t.green }
            }}>
            בחר קובץ
            <input accept="image/png, image/gif, image/jpeg" type="file" hidden
              onChange={(e) => handleSlotUpload(imageType, imageKey, e)} />
          </Button>
          {!isMarkedForRemoval && currentUrl && (
            <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
              <Box component="img" src={currentUrl}
                sx={{width: 60, height: 40, objectFit: "cover", border: `1px solid ${t.border}`, borderRadius: t.rs}} />
              <IconButton onClick={() => handleSlotRemove(imageType, imageKey)} size="small"
                sx={{color: t.red, "&:hover": {backgroundColor: t.redLight}}}>
                <CloseIcon fontSize="small"/>
              </IconButton>
            </Box>
          )}
          {isMarkedForRemoval && <Typography sx={{color: t.t3, fontSize: "13px"}}>יוסר בשמירה</Typography>}
        </Box>
      </Box>
    );
  };

  return (
      <Paper
          sx={{
            backgroundColor: t.white,
            border: `1px solid ${t.border}`,
            borderRadius: "8px",
            direction: "rtl"
          }}
          elevation={0}
      >
        {/* Edit Dialog */}
        <Dialog
            open={editOpen}
            onClose={handleClose}
            PaperProps={{
                sx: {
                    borderRadius: t.r,
                    minWidth: {xs: "95%", md: "960px"},
                    maxWidth: {xs: "95%", md: "1020px"},
                    maxHeight: {xs: "90vh", md: "85vh"},
                    overflowY: "auto"
                }
            }}
        >
            <RTL>
                <Box dir="rtl">
                    {/* Header */}
                    <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "16px 20px",
                        backgroundColor: t.bg,
                        borderBottom: `1px solid ${t.border}`
                    }}>
                        <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                            <PaletteIcon sx={{color: t.t2, fontSize: "20px"}} />
                            <Typography sx={{ fontWeight: 600, fontSize: "16px", color: t.text }}>
                                עריכת מיתוג
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={handleClose}
                            size="small"
                            sx={{ color: t.t3, "&:hover": { backgroundColor: t.greenLight, color: t.text } }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    {/* Content */}
                    <form onSubmit={formik.handleSubmit}>
                      <DialogContent sx={{padding: "24px 20px"}}>
                        <Box sx={{
                          display: "flex",
                          flexDirection: {xs: "column", md: "row-reverse"},
                          gap: "24px",
                        }}>
                        <Box sx={{flex: 1}}>
                        <Box sx={{
                          display: "grid",
                          gridTemplateColumns: {xs: "1fr", md: "1fr 1fr"},
                          gap: "16px",
                        }}>
                            {/* App Name */}
                            <Box>
                                <Typography sx={labelSx}>שם האפליקציה</Typography>
                                <TextField sx={inputSx} fullWidth size="small"
                                    id="brandingAppName"
                                    value={formik.values.brandingAppName}
                                    onChange={formik.handleChange}
                                    placeholder="הכנס שם האפליקציה"
                                    variant="outlined" />
                            </Box>

                            {/* White-label toggle — SYSTEM_ADMIN only. UI-only; reveals the
                                bundle id + store URL fields. Does not persist on its own. */}
                            {canEditBundleId && (
                              <Box sx={{gridColumn: {md: "1 / -1"}}}>
                                <FormControlLabel
                                    control={
                                      <Checkbox
                                          checked={isWhiteLabel}
                                          onChange={(e) => setIsWhiteLabel(e.target.checked)}
                                          sx={{color: t.green, "&.Mui-checked": {color: t.green}}}
                                      />
                                    }
                                    label={
                                      <Typography sx={{fontSize: "13px", fontWeight: 600, color: t.t2}}>
                                        אפליקציית White-Label
                                      </Typography>
                                    }
                                />
                              </Box>
                            )}

                            {/* White-label Bundle ID — SYSTEM_ADMIN only.
                                Server enforces the same rule and silently drops the field for other roles. */}
                            {canEditBundleId && isWhiteLabel && (
                              <Box>
                                <Typography sx={labelSx}>
                                  Bundle ID (אפליקציית White-Label)
                                </Typography>
                                <TextField sx={inputSx} fullWidth size="small"
                                    id="bundleId"
                                    value={formik.values.bundleId}
                                    onChange={formik.handleChange}
                                    placeholder="com.acme.coach (השאר ריק לאפליקציה משותפת)"
                                    variant="outlined"
                                    inputProps={{ dir: "ltr" }} />
                                <Typography sx={{...labelSx, marginTop: "4px", marginBottom: 0, fontSize: "11px"}}>
                                  כשמוגדר, רק משתמשי החברה הזו יוכלו להתחבר לאפליקציה עם ה-Bundle ID הזה.
                                </Typography>
                              </Box>
                            )}

                            {/* White-label app store links — SYSTEM_ADMIN only.
                                Injected into the welcome email; empty falls back to the shared links. */}
                            {canEditBundleId && isWhiteLabel && (
                              <Box>
                                <Typography sx={labelSx}>
                                  קישור App Store (Apple)
                                </Typography>
                                <TextField sx={inputSx} fullWidth size="small"
                                    id="appleUrl"
                                    value={formik.values.appleUrl}
                                    onChange={formik.handleChange}
                                    placeholder="https://apps.apple.com/... (השאר ריק לקישור ברירת מחדל)"
                                    variant="outlined"
                                    inputProps={{ dir: "ltr" }} />
                              </Box>
                            )}

                            {canEditBundleId && isWhiteLabel && (
                              <Box>
                                <Typography sx={labelSx}>
                                  קישור Google Play
                                </Typography>
                                <TextField sx={inputSx} fullWidth size="small"
                                    id="googleUrl"
                                    value={formik.values.googleUrl}
                                    onChange={formik.handleChange}
                                    placeholder="https://play.google.com/... (השאר ריק לקישור ברירת מחדל)"
                                    variant="outlined"
                                    inputProps={{ dir: "ltr" }} />
                                <Typography sx={{...labelSx, marginTop: "4px", marginBottom: 0, fontSize: "11px"}}>
                                  הקישורים נשלחים במייל הברוכים הבאים למתאמן חדש. אם ריק — נשלחים קישורי ברירת המחדל.
                                </Typography>
                              </Box>
                            )}

                            {/* Per-brand force-upgrade floor — SYSTEM_ADMIN only.
                                Empty = brand is never force-updated. */}
                            {canEditBundleId && isWhiteLabel && (
                              <Box>
                                <Typography sx={labelSx}>
                                  גרסת מינימום iOS (עדכון כפוי)
                                </Typography>
                                <TextField sx={inputSx} fullWidth size="small"
                                    id="minVersionIos"
                                    value={formik.values.minVersionIos}
                                    onChange={formik.handleChange}
                                    placeholder="3.4.0 (השאר ריק — ללא עדכון כפוי)"
                                    variant="outlined"
                                    inputProps={{ dir: "ltr" }} />
                              </Box>
                            )}

                            {canEditBundleId && isWhiteLabel && (
                              <Box>
                                <Typography sx={labelSx}>
                                  גרסת מינימום Android (עדכון כפוי)
                                </Typography>
                                <TextField sx={inputSx} fullWidth size="small"
                                    id="minVersionAndroid"
                                    value={formik.values.minVersionAndroid}
                                    onChange={formik.handleChange}
                                    placeholder="55.0 (השאר ריק — ללא עדכון כפוי)"
                                    variant="outlined"
                                    inputProps={{ dir: "ltr" }} />
                                <Typography sx={{...labelSx, marginTop: "4px", marginBottom: 0, fontSize: "11px"}}>
                                  אפליקציה שגרסתה נמוכה מהמינימום תיחסם בהפעלה עד לעדכון. ריק — ללא חסימה.
                                </Typography>
                              </Box>
                            )}

                            {/* Color Picker */}
                            <Box>
                                <Typography sx={labelSx}>צבע מותג</Typography>
                                <Box sx={{display: "flex", alignItems: "center", gap: 2}}>
                                    <TextField
                                        type="color"
                                        id="brandingColor"
                                        value={formik.values.brandingColor}
                                        onChange={formik.handleChange}
                                        sx={{
                                            width: 48, height: 36,
                                            "& input": { padding: "4px", cursor: "pointer" },
                                            "& .MuiOutlinedInput-root": { borderRadius: t.rs },
                                            "& .MuiOutlinedInput-notchedOutline": { borderColor: t.border },
                                        }}
                                    />
                                    <TextField sx={{...inputSx, width: 120}} size="small"
                                        value={formik.values.brandingColor}
                                        onChange={formik.handleChange}
                                        id="brandingColor"
                                        placeholder="#000000" />
                                </Box>
                            </Box>

                            {/* Logo Upload */}
                            <Box>
                                <Typography sx={labelSx}>לוגו</Typography>
                                <Box sx={{display: "flex", alignItems: "center", gap: 2}}>
                                    <Button variant="outlined" component="label"
                                        sx={{
                                            minWidth: 100, borderColor: t.border, color: t.t2,
                                            fontSize: "13px", borderRadius: t.rs, textTransform: "none",
                                            "&:hover": { borderColor: t.green, color: t.green }
                                        }}>
                                        בחר קובץ
                                        <input accept="image/png, image/gif, image/jpeg" type="file" hidden onChange={handleLogoUpload} />
                                    </Button>
                                    {!removeLogo && (logoPreview || company?.brandingLogoUrl) && (
                                        <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                            <Box component="img" src={logoPreview || company?.brandingLogoUrl}
                                                sx={{width: 48, height: 48, objectFit: "contain", border: `1px solid ${t.border}`, borderRadius: t.rs}} />
                                            <IconButton onClick={handleRemoveLogo} size="small"
                                                sx={{color: t.red, "&:hover": {backgroundColor: t.redLight}}}>
                                                <CloseIcon fontSize="small"/>
                                            </IconButton>
                                        </Box>
                                    )}
                                    {removeLogo && <Typography sx={{color: t.t3, fontSize: "13px"}}>יוסר בשמירה</Typography>}
                                </Box>
                            </Box>

                            {/* Background Upload */}
                            <Box>
                                <Typography sx={labelSx}>תמונת רקע</Typography>
                                <Box sx={{display: "flex", alignItems: "center", gap: 2}}>
                                    <Button variant="outlined" component="label"
                                        sx={{
                                            minWidth: 100, borderColor: t.border, color: t.t2,
                                            fontSize: "13px", borderRadius: t.rs, textTransform: "none",
                                            "&:hover": { borderColor: t.green, color: t.green }
                                        }}>
                                        בחר קובץ
                                        <input accept="image/png, image/gif, image/jpeg" type="file" hidden onChange={handleBackgroundUpload} />
                                    </Button>
                                    {!removeBackground && (backgroundPreview || company?.brandingBackgroundImageUrl) && (
                                        <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                            <Box component="img" src={backgroundPreview || company?.brandingBackgroundImageUrl}
                                                sx={{width: 80, height: 48, objectFit: "cover", border: `1px solid ${t.border}`, borderRadius: t.rs}} />
                                            <IconButton onClick={handleRemoveBackground} size="small"
                                                sx={{color: t.red, "&:hover": {backgroundColor: t.redLight}}}>
                                                <CloseIcon fontSize="small"/>
                                            </IconButton>
                                        </Box>
                                    )}
                                    {removeBackground && <Typography sx={{color: t.t3, fontSize: "13px"}}>יוסר בשמירה</Typography>}
                                </Box>
                            </Box>

                            {/* Show Workout Images Toggle */}
                            <Box sx={{
                                gridColumn: {xs: "1", md: "1 / -1"},
                                display: "flex", alignItems: "center", justifyContent: "space-between",
                                padding: "10px 14px", backgroundColor: t.bg,
                                border: `1px solid ${t.border}`, borderRadius: t.rs,
                            }}>
                                <Typography sx={{fontSize: "13px", fontWeight: 500, color: t.t2}}>
                                    הצג תמונות אימון
                                </Typography>
                                <Switch
                                    checked={formik.values.showWorkoutImages}
                                    onChange={(e) => formik.setFieldValue('showWorkoutImages', e.target.checked)}
                                    sx={{
                                        "& .MuiSwitch-switchBase.Mui-checked": {color: t.green},
                                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {backgroundColor: t.green}
                                    }}
                                />
                            </Box>
                        </Box>

                        {/* Week Images Section */}
                        <Box sx={{mt: 3}}>
                          <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 1}}>
                            <WeekIcon sx={{color: t.t2, fontSize: "18px"}} />
                            <Typography sx={{fontWeight: 600, fontSize: "14px", color: t.text}}>
                              תמונות שבועות
                            </Typography>
                          </Box>
                          <Typography sx={{fontSize: "12px", color: t.t3, mb: 2}}>
                            התמונות יוצגו בסדר מחזורי לפי שבועות התוכנית. מומלץ להעלות לפחות 7 תמונות.
                          </Typography>
                          <Box sx={{
                            display: "grid",
                            gridTemplateColumns: {xs: "1fr", md: "1fr 1fr"},
                            gap: "16px",
                          }}>
                            {existingWeekImages.map((img, i) =>
                              renderImageSlot("WEEK", img.imageKey, `תמונה ${parseInt(img.imageKey) + 1}`)
                            )}
                            {newWeekSlots.map((slot, i) => {
                              const slotKey = `WEEK:${slot.key}`;
                              const slotState = imageSlots[slotKey];
                              return (
                                <Box key={slotKey}>
                                  <Typography sx={labelSx}>{`תמונה ${parseInt(slot.key) + 1}`}</Typography>
                                  <Box sx={{display: "flex", alignItems: "center", gap: 2}}>
                                    <Button variant="outlined" component="label"
                                      sx={{
                                        minWidth: 100, borderColor: t.border, color: t.t2,
                                        fontSize: "13px", borderRadius: t.rs, textTransform: "none",
                                        "&:hover": { borderColor: t.green, color: t.green }
                                      }}>
                                      בחר קובץ
                                      <input accept="image/png, image/gif, image/jpeg" type="file" hidden
                                        onChange={(e) => handleSlotUpload("WEEK", slot.key, e)} />
                                    </Button>
                                    {slotState?.preview && (
                                      <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                        <Box component="img" src={slotState.preview}
                                          sx={{width: 60, height: 40, objectFit: "cover", border: `1px solid ${t.border}`, borderRadius: t.rs}} />
                                        <IconButton onClick={() => {
                                          setNewWeekSlots(prev => prev.filter((_, idx) => idx !== i));
                                          setImageSlots(prev => {
                                            const next = {...prev};
                                            delete next[slotKey];
                                            return next;
                                          });
                                        }} size="small"
                                          sx={{color: t.red, "&:hover": {backgroundColor: t.redLight}}}>
                                          <CloseIcon fontSize="small"/>
                                        </IconButton>
                                      </Box>
                                    )}
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                          <Button
                            startIcon={<AddIcon />}
                            onClick={() => setNewWeekSlots(prev => [...prev, {key: String(nextWeekImageIndex)}])}
                            sx={{
                              mt: 2, color: t.green, fontSize: "13px", textTransform: "none",
                              "&:hover": { backgroundColor: t.greenLight }
                            }}>
                            הוסף תמונה
                          </Button>
                        </Box>

                        {/* Workout Images Section */}
                        <Box sx={{mt: 3}}>
                          <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 2}}>
                            <WorkoutIcon sx={{color: t.t2, fontSize: "18px"}} />
                            <Typography sx={{fontWeight: 600, fontSize: "14px", color: t.text}}>
                              תמונות אימונים
                            </Typography>
                          </Box>
                          <Box sx={{
                            display: "grid",
                            gridTemplateColumns: {xs: "1fr", md: "1fr 1fr"},
                            gap: "16px",
                          }}>
                            {WORKOUT_IMAGE_SLOTS.map(slot => renderImageSlot("WORKOUT", slot.key, slot.label))}
                          </Box>
                        </Box>

                        </Box>

                        {/* Phone Preview */}
                        <Box sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          flexShrink: 0,
                        }}>
                          <Typography sx={{fontSize: "13px", fontWeight: 500, color: t.t2, mb: 1}}>
                            תצוגה מקדימה
                          </Typography>
                          <BrandingPhonePreview
                            brandingColor={formik.values.brandingColor}
                            brandingAppName={formik.values.brandingAppName}
                            logoUrl={removeLogo ? null : (logoPreview || company?.brandingLogoUrl || null)}
                            backgroundImageUrl={removeBackground ? null : (backgroundPreview || company?.brandingBackgroundImageUrl || null)}
                          />
                        </Box>
                        </Box>
                      </DialogContent>

                      {/* Footer */}
                      <Box sx={{
                          display: "flex", justifyContent: "flex-start", gap: 1.5,
                          padding: "16px 20px", borderTop: `1px solid ${t.border}`, backgroundColor: t.bg
                      }}>
                          <Button
                              type="submit"
                              variant="contained"
                              disabled={saving}
                              sx={{
                                  backgroundColor: t.green, color: t.white, fontSize: "13px", fontWeight: 600,
                                  padding: "8px 24px", borderRadius: t.rs, textTransform: "none",
                                  "&:hover": { backgroundColor: t.greenDark },
                                  "&.Mui-disabled": { backgroundColor: t.border, color: t.t3 }
                              }}>
                              {saving ? 'שומר...' : 'שמור'}
                          </Button>
                          <Button onClick={handleClose} variant="outlined"
                              sx={{
                                  borderColor: t.border, color: t.t2, fontSize: "13px", fontWeight: 500,
                                  padding: "8px 20px", borderRadius: t.rs, textTransform: "none",
                                  "&:hover": { borderColor: t.t2, backgroundColor: t.greenLight }
                              }}>
                              ביטול
                          </Button>
                      </Box>
                    </form>
                </Box>
            </RTL>
        </Dialog>

        {/* Header */}
        <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: { xs: "12px 16px", md: "16px 20px" },
              backgroundColor: t.bg,
              borderBottom: `1px solid ${t.border}`
            }}
        >
          <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
            <PaletteIcon sx={{color: t.t2, fontSize: { xs: "18px", md: "20px" }}}/>
            <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "14px", md: "16px" },
                  color: t.text,
                  fontFamily: '"Heebo", "Roboto", sans-serif'
                }}
            >
              מיתוג
            </Typography>
          </Box>
          <IconButton
              size="small"
              onClick={() => setEditOpen(true)}
              sx={{color: t.t2, "&:hover": {color: t.green}}}
          >
            <EditIcon sx={{fontSize: { xs: "18px", md: "20px" }}}/>
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{padding: { xs: "16px", md: "20px" }}}>
          <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                  lg: "repeat(4, 1fr)"
                },
                gap: "16px 24px",
                rowGap: "12px"
              }}
          >
            <BrandingRow
                icon={<TitleIcon/>}
                label="שם האפליקציה"
                value={company?.brandingAppName || "לא הוגדר"}
            />

            {canEditBundleId && (
              <BrandingRow
                  icon={<TitleIcon/>}
                  label="Bundle ID"
                  value={
                    company?.bundleId
                      ? <Box sx={{direction: "ltr", textAlign: "left"}}>{company.bundleId}</Box>
                      : "אפליקציה משותפת"
                  }
              />
            )}

            {canEditBundleId && (
              <BrandingRow
                  icon={<TitleIcon/>}
                  label="קישור App Store"
                  value={
                    company?.appleUrl
                      ? <Box sx={{direction: "ltr", textAlign: "left", wordBreak: "break-all"}}>{company.appleUrl}</Box>
                      : "קישור ברירת מחדל"
                  }
              />
            )}

            {canEditBundleId && (
              <BrandingRow
                  icon={<TitleIcon/>}
                  label="קישור Google Play"
                  value={
                    company?.googleUrl
                      ? <Box sx={{direction: "ltr", textAlign: "left", wordBreak: "break-all"}}>{company.googleUrl}</Box>
                      : "קישור ברירת מחדל"
                  }
              />
            )}

            <BrandingRow
                icon={<PaletteIcon/>}
                label="צבע מותג"
                value={
                  company?.brandingColor ? (
                      <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                        <Box
                            sx={{
                              width: 20,
                              height: 20,
                              backgroundColor: company.brandingColor,
                              borderRadius: "4px",
                              border: `1px solid ${t.border}`
                            }}
                        />
                        {company.brandingColor}
                      </Box>
                  ) : "לא הוגדר"
                }
            />

            <BrandingRow
                icon={<ImageIcon/>}
                label="לוגו"
                value={
                  company?.brandingLogoUrl ? (
                      <Box
                          component="img"
                          src={company.brandingLogoUrl}
                          sx={{
                            width: 40,
                            height: 40,
                            objectFit: "contain",
                            borderRadius: "4px"
                          }}
                      />
                  ) : "לא הוגדר"
                }
            />

            <BrandingRow
                icon={<ImageIcon/>}
                label="תמונת רקע"
                value={
                  company?.brandingBackgroundImageUrl ? (
                      <Box
                          component="img"
                          src={company.brandingBackgroundImageUrl}
                          sx={{
                            width: 60,
                            height: 40,
                            objectFit: "cover",
                            borderRadius: "4px"
                          }}
                      />
                  ) : "לא הוגדר"
                }
            />

            <BrandingRow
                icon={<VisibilityIcon/>}
                label="תמונות אימון"
                value={company?.showWorkoutImages === false ? "מוסתר" : "מוצג"}
            />
          </Box>

          {/* Week & Workout branding images summary */}
          {company?.brandingImages?.length > 0 && (
            <>
              <Typography sx={{fontSize: "13px", fontWeight: 600, color: t.text, mt: 2, mb: 1}}>
                תמונות שבועות ואימונים
              </Typography>
              <Box sx={{display: "flex", flexWrap: "wrap", gap: 1.5}}>
                {company.brandingImages.map(img => {
                  const slotLabel = img.imageType === "WEEK"
                    ? `תמונה ${parseInt(img.imageKey) + 1}`
                    : WORKOUT_IMAGE_SLOTS.find(s => s.key === img.imageKey)?.label;
                  return (
                    <Box key={`${img.imageType}:${img.imageKey}`} sx={{textAlign: "center"}}>
                      <Box component="img" src={img.imageUrl}
                        sx={{width: 60, height: 40, objectFit: "cover", borderRadius: "4px", border: `1px solid ${t.border}`}} />
                      <Typography sx={{fontSize: "11px", color: t.t3, mt: 0.5}}>
                        {slotLabel || img.imageKey}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </>
          )}
        </Box>
      </Paper>
  );
};

const BrandingRow = ({icon, label, value}) => (
    <Box sx={{display: "flex", alignItems: "flex-start", gap: 1.5, minHeight: "24px"}}>
      <Box
          sx={{
            color: t.t2,
            display: "flex",
            alignItems: "center",
            fontSize: "14px",
            flexShrink: 0,
            marginTop: "1px"
          }}
      >
        {icon}
      </Box>
      <Box sx={{flex: 1, minWidth: 0}}>
        <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 500,
              color: t.t2,
              marginBottom: "2px",
              textAlign: "right"
            }}
        >
          {label}:
        </Typography>
        <Box
            sx={{
              fontSize: "14px",
              fontWeight: 600,
              color: t.text,
              lineHeight: "18px",
              wordBreak: "break-word",
              textAlign: "right"
            }}
        >
          {value}
        </Box>
      </Box>
    </Box>
);

export default CompanyBrandingSection;
